import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

/* -------------------------------------------------------------------------
 * DataTable — generic table shell reused across the Restaurant Manager
 * portal (Redemptions page, Recent Redemptions preview, Top Customers).
 * Columns declare their own width + custom cell renderer.
 * Supports loading skeleton, empty state, and infinite scroll.
 * ---------------------------------------------------------------------- */

const DataTable = ({
  columns,
  rows,
  getRowKey,
  loading = false,
  loadingMore = false,
  onLoadMore = null,
  hasMore = false,
  compact = false,
  maxHeight = null,
}) => {
  const gridTemplateColumns = columns.map((col) => col.width || "1fr").join(" ");
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef(null);

  // Calculate height based on props
  const tableHeight = maxHeight ? maxHeight : compact ? 'auto' : 'calc(100vh - 220px)';
  const tableMaxHeight = maxHeight ? maxHeight : compact ? '400px' : 'calc(100vh - 220px)';

  // Calculate minimum width to prevent column shrinking
  const minWidth = columns.reduce((total, col) => {
    const width = col.width || "1fr";
    if (width.endsWith('px')) {
      return total + parseInt(width);
    } else if (width.endsWith('fr')) {
      return total + 120; // Minimum 120px per fr unit
    }
    return total + 120;
  }, 0);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled to 90% of the bottom
    if (hasMore && onLoadMore && scrollPercentage >= 0.9 && !isScrolling) {
      setIsScrolling(true);
      onLoadMore();
      setTimeout(() => setIsScrolling(false), 500);
    }
  };

  // Render skeleton rows for loading state
  const renderSkeleton = () => {
    return Array(5).fill(null).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="rp-table__row rp-table__row--skeleton"
        style={{ gridTemplateColumns }}
      >
        {columns.map((col) => (
          <span key={col.key} className="rp-table__cell rp-table__cell--skeleton">
            <div className="rp-skeleton" />
          </span>
        ))}
      </div>
    ));
  };

  // Render empty state
  const renderEmpty = () => (
    <div className="rp-table__empty">
      <div className="rp-table__empty-icon">📋</div>
      <div className="rp-table__empty-text">No data found</div>
    </div>
  );

  return (
    <div
      className={`rp-table ${compact ? 'rp-table--compact' : ''}`}
      style={{ overflowX: 'auto', height: tableHeight, maxHeight: tableMaxHeight, overflowY: compact ? 'auto' : 'auto' }}
      ref={scrollRef}
      onScroll={onLoadMore ? handleScroll : undefined}
    >
      <style>{`
        ${!compact ? `
        @media (max-width: 576px) {
          .rp-table {
            height: calc(100vh - 300px) !important;
            max-height: calc(100vh - 300px) !important;
          }
        }
        @media (min-width: 577px) and (max-width: 991px) {
          .rp-table {
            height: calc(100vh - 280px) !important;
            max-height: calc(100vh - 280px) !important;
          }
        }
        ` : ''}
        .rp-table__cell {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }
      `}</style>
      <div
        className="rp-table__body"
        style={{ minWidth: `${minWidth}px`, overflow: 'visible' }}
      >
        <div className="rp-table__row rp-table__row--header" style={{ gridTemplateColumns }}>
          {columns.map((col) => (
            <span
              key={col.key}
              className={`rp-table__cell rp-table__cell--header rp-table__cell--${col.align || "left"}`}
            >
              {col.label}
            </span>
          ))}
        </div>
        {loading ? (
          renderSkeleton()
        ) : rows.length === 0 ? (
          renderEmpty()
        ) : (
          rows.map((row, index) => (
            <div
              key={getRowKey ? getRowKey(row) : index}
              className="rp-table__row"
              style={{ gridTemplateColumns }}
            >
              {columns.map((col) => (
                <span key={col.key} className={`rp-table__cell rp-table__cell--${col.align || "left"} ${col.className || ""}`}>
                  {col.render ? col.render(row) : row[col.key]}
                </span>
              ))}
            </div>
          ))
        )}
        {(hasMore || loadingMore) && !loading && rows.length > 0 && (
          <div className="rp-table__loading-more">
            <div className="rp-table__loading-spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  getRowKey: PropTypes.func,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  compact: PropTypes.bool,
  maxHeight: PropTypes.string,
};

export default DataTable;
